import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Slider } from '../components/ui/slider';
import { 
  ArrowLeft, 
  Music, 
  Play, 
  Pause, 
  Volume2, 
  VolumeX,
  Lock,
  Crown,
  Settings
} from 'lucide-react';
import { toast } from 'sonner';
import api, { BACKEND_BASE_URL } from '../lib/api';

const FREE_PREVIEW_SECONDS = 30;

// Format time as mm:ss
const formatTime = (seconds) => {
  if (!seconds || isNaN(seconds)) return '0:00';
  const mins = Math.floor(seconds / 60);
  const secs = Math.floor(seconds % 60);
  return `${mins}:${secs.toString().padStart(2, '0')}`;
};

export const ShedTracksPage = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const audioRef = useRef(null);
  
  const [tracks, setTracks] = useState([]);
  const [filters, setFilters] = useState({ keys: [], styles: [] });
  const [loading, setLoading] = useState(true);
  
  // Filter state
  const [selectedKey, setSelectedKey] = useState('any');
  const [selectedStyle, setSelectedStyle] = useState('any');
  
  // Player state
  const [currentTrack, setCurrentTrack] = useState(null);
  const [isPlaying, setIsPlaying] = useState(false);
  const [currentTime, setCurrentTime] = useState(0);
  const [duration, setDuration] = useState(0);
  const [volume, setVolume] = useState(0.8);
  const [isMuted, setIsMuted] = useState(false);
  const [showProOverlay, setShowProOverlay] = useState(false);

  const isPro = user?.is_pro;

  useEffect(() => {
    fetchTracks();
    fetchFilters();
  }, []);

  const fetchTracks = async () => {
    try {
      const response = await api.get(`/shed-tracks`);
      setTracks(response.data);
    } catch (error) {
      console.error('Error fetching tracks:', error);
      toast.error('Failed to load tracks');
    } finally {
      setLoading(false);
    }
  };

  const fetchFilters = async () => {
    try {
      const response = await api.get(`/shed-tracks/filters`);
      setFilters(response.data);
    } catch (error) {
      console.error('Error fetching filters:', error);
    }
  };

  // Filter tracks
  const filteredTracks = tracks.filter(track => {
    if (selectedKey !== 'any' && track.key !== selectedKey) return false;
    if (selectedStyle !== 'any' && track.style !== selectedStyle) return false;
    return true;
  });

  // Play a track
  const playTrack = useCallback((track) => {
    if (currentTrack?.track_id === track.track_id && isPlaying) {
      // Pause current track
      audioRef.current?.pause();
      setIsPlaying(false);
    } else {
      // Play new track
      setCurrentTrack(track);
      setCurrentTime(0);
      setShowProOverlay(false);
      
      // Load and play audio
      if (audioRef.current) {
        // Determine which URL to use based on Pro status and track storage type
        let audioUrl;
        
        if (track.storage_type === 'gridfs') {
          // New GridFS-based tracks: use preview URL for free, full for Pro
          if (isPro && track.full_url) {
            audioUrl = track.full_url;
            console.log('Loading FULL audio (Pro user):', track.title);
          } else if (track.preview_url) {
            audioUrl = track.preview_url;
            console.log('Loading PREVIEW audio (30s):', track.title);
          }
        } else {
          // Legacy tracks: use playback_url or audio_url
          audioUrl = track.playback_url || track.preview_url || track.audio_url;
          console.log('Loading LEGACY audio:', track.title);
        }
        
        // Ensure full URL with backend base
        if (audioUrl && audioUrl.startsWith('/api/')) {
          audioUrl = `${BACKEND_BASE_URL}${audioUrl}`;
        } else if (audioUrl && audioUrl.startsWith('/')) {
          audioUrl = `${BACKEND_BASE_URL}/api${audioUrl}`;
        }
        
        if (!audioUrl) {
          toast.error('Audio not available for this track');
          return;
        }
        
        console.log('Loading audio URL:', audioUrl);
        audioRef.current.src = audioUrl;
        
        // Add error handler
        audioRef.current.onerror = (e) => {
          console.error('Audio load error:', e, audioUrl);
          const errorCode = audioRef.current?.error?.code;
          let errorMsg = 'Could not play audio';
          
          if (errorCode === 4) {
            errorMsg = 'Audio file not found or format not supported';
          } else if (errorCode === 2) {
            errorMsg = 'Network error loading audio';
          }
          
          toast.error(errorMsg);
          setIsPlaying(false);
        };
        
        audioRef.current.load();
        audioRef.current.play().catch(e => {
          console.error('Playback error:', e);
          toast.error('Could not play audio. Try refreshing the page.');
        });
        setIsPlaying(true);
      }
    }
  }, [currentTrack, isPlaying, isPro]);

  // Handle time update
  const handleTimeUpdate = useCallback(() => {
    if (!audioRef.current) return;
    
    const time = audioRef.current.currentTime;
    setCurrentTime(time);
    
    // Pro gating: stop at 30 seconds for free users
    if (!isPro && time >= FREE_PREVIEW_SECONDS) {
      audioRef.current.pause();
      setIsPlaying(false);
      setShowProOverlay(true);
    }
  }, [isPro]);

  // Handle duration loaded
  const handleLoadedMetadata = useCallback(() => {
    if (audioRef.current) {
      setDuration(audioRef.current.duration);
    }
  }, []);

  // Handle track ended
  const handleEnded = useCallback(() => {
    setIsPlaying(false);
    setCurrentTime(0);
  }, []);

  // Seek to position
  const handleSeek = useCallback((value) => {
    if (!audioRef.current) return;
    const newTime = value[0];
    
    // Free users can't seek past 30 seconds
    if (!isPro && newTime > FREE_PREVIEW_SECONDS) {
      setShowProOverlay(true);
      return;
    }
    
    audioRef.current.currentTime = newTime;
    setCurrentTime(newTime);
  }, [isPro]);

  // Toggle play/pause
  const togglePlayPause = useCallback(() => {
    if (!audioRef.current || !currentTrack) return;
    
    if (isPlaying) {
      audioRef.current.pause();
      setIsPlaying(false);
    } else {
      // Check if free user at limit
      if (!isPro && currentTime >= FREE_PREVIEW_SECONDS) {
        setShowProOverlay(true);
        return;
      }
      audioRef.current.play().catch(e => console.error(e));
      setIsPlaying(true);
    }
  }, [isPlaying, isPro, currentTime, currentTrack]);

  // Handle volume change
  const handleVolumeChange = useCallback((value) => {
    const newVolume = value[0];
    setVolume(newVolume);
    if (audioRef.current) {
      audioRef.current.volume = newVolume;
    }
    setIsMuted(newVolume === 0);
  }, []);

  // Toggle mute
  const toggleMute = useCallback(() => {
    if (audioRef.current) {
      if (isMuted) {
        audioRef.current.volume = volume || 0.8;
        setIsMuted(false);
      } else {
        audioRef.current.volume = 0;
        setIsMuted(true);
      }
    }
  }, [isMuted, volume]);

  return (
    <div className="min-h-screen bg-ss-bg pb-32">
      <div className="noise-overlay"></div>

      {/* Hidden audio element */}
      <audio
        ref={audioRef}
        onTimeUpdate={handleTimeUpdate}
        onLoadedMetadata={handleLoadedMetadata}
        onEnded={handleEnded}
        preload="metadata"
      />

      {/* Header */}
      <header className="sticky top-0 z-50 glass-panel">
        <div className="max-w-7xl mx-auto px-4 py-4 flex items-center justify-between">
          <div className="flex items-center gap-4">
            <Button
              variant="ghost"
              onClick={() => navigate('/dashboard')}
              data-testid="back-btn"
              className="text-ss-muted hover:text-ss-text"
            >
              <ArrowLeft className="w-5 h-5" />
            </Button>
            <div>
              <h1 className="font-heading text-xl font-bold tracking-tight uppercase text-ss-text">
                Shed Tracks
              </h1>
              <p className="text-xs text-ss-muted">Backing tracks for Sacred Steel practice</p>
            </div>
          </div>
          
          {user && !isPro && (
            <Button
              onClick={() => navigate('/upgrade')}
              className="bg-ss-gold hover:bg-ss-gold/90 text-black font-semibold"
            >
              <Crown className="w-4 h-4 mr-2" />
              Unlock Full Tracks
            </Button>
          )}
          
          {user?.is_pro && (
            <div className="flex items-center gap-2 text-ss-gold">
              <Crown className="w-4 h-4" />
              <span className="text-sm font-semibold">Pro</span>
            </div>
          )}
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-4 py-8">
        {/* Filters */}
        <Card className="bg-ss-surface border-ss-border mb-8">
          <CardContent className="py-4">
            <div className="flex flex-wrap items-center gap-4">
              <div className="flex items-center gap-2">
                <span className="text-sm text-ss-muted">Key:</span>
                <Select value={selectedKey} onValueChange={setSelectedKey}>
                  <SelectTrigger className="w-24 bg-ss-bg border-ss-border text-ss-text" data-testid="filter-key">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent className="bg-ss-surface border-ss-border">
                    <SelectItem value="any" className="text-ss-text">Any</SelectItem>
                    {filters.keys.map(key => (
                      <SelectItem key={key} value={key} className="text-ss-text">{key}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div className="flex items-center gap-2">
                <span className="text-sm text-ss-muted">Style:</span>
                <Select value={selectedStyle} onValueChange={setSelectedStyle}>
                  <SelectTrigger className="w-32 bg-ss-bg border-ss-border text-ss-text" data-testid="filter-style">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent className="bg-ss-surface border-ss-border">
                    <SelectItem value="any" className="text-ss-text">Any</SelectItem>
                    {filters.styles.map(style => (
                      <SelectItem key={style} value={style} className="text-ss-text capitalize">{style}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div className="ml-auto text-sm text-ss-muted">
                {filteredTracks.length} track{filteredTracks.length !== 1 ? 's' : ''}
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Track List */}
        {loading ? (
          <div className="flex justify-center py-20">
            <div className="w-12 h-12 border-4 border-ss-cyan border-t-transparent rounded-full animate-spin"></div>
          </div>
        ) : filteredTracks.length === 0 ? (
          <Card className="bg-ss-surface border-ss-border">
            <CardContent className="py-12 text-center">
              <Music className="w-12 h-12 text-ss-muted mx-auto mb-4" />
              <p className="text-ss-muted">No tracks found. Try adjusting your filters.</p>
            </CardContent>
          </Card>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {filteredTracks.map(track => (
              <Card 
                key={track.track_id}
                className={`bg-ss-surface border-ss-border hover:border-ss-cyan/30 transition-all duration-200 ${
                  currentTrack?.track_id === track.track_id ? 'border-ss-cyan' : ''
                }`}
                data-testid={`track-card-${track.track_id}`}
              >
                <CardContent className="p-6">
                  <div className="flex items-start justify-between mb-4">
                    <div className="flex-1">
                      <h3 className="font-heading text-lg font-bold text-ss-text mb-2">
                        {track.title}
                      </h3>
                      <div className="flex flex-wrap gap-2">
                        {track.key && (
                          <span className="text-xs px-2 py-1 bg-ss-cyan/20 text-ss-cyan rounded">
                            Key: {track.key}
                          </span>
                        )}
                        {track.tempo && (
                          <span className="text-xs px-2 py-1 bg-ss-purple/20 text-ss-purple rounded">
                            {track.tempo} BPM
                          </span>
                        )}
                        {track.style && (
                          <span className="text-xs px-2 py-1 bg-ss-surface-highlight text-ss-muted rounded capitalize">
                            {track.style}
                          </span>
                        )}
                      </div>
                    </div>
                  </div>

                  <div className="flex items-center justify-between">
                    <span className="text-sm text-ss-muted">
                      {formatTime(track.duration)}
                      {!isPro && (
                        <span className="ml-2 text-ss-gold">
                          (30s preview)
                        </span>
                      )}
                    </span>
                    <Button
                      onClick={() => playTrack(track)}
                      data-testid={`play-${track.track_id}`}
                      size="lg"
                      className={`h-14 w-14 rounded-full ${
                        currentTrack?.track_id === track.track_id && isPlaying
                          ? 'bg-ss-red hover:bg-ss-red/90'
                          : 'bg-ss-cyan hover:bg-ss-cyan/90'
                      } text-black`}
                    >
                      {currentTrack?.track_id === track.track_id && isPlaying ? (
                        <Pause className="w-6 h-6" />
                      ) : (
                        <Play className="w-6 h-6 ml-1" />
                      )}
                    </Button>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        )}
      </main>

      {/* Persistent Audio Player */}
      {currentTrack && (
        <div className="fixed bottom-0 left-0 right-0 z-50 glass-panel border-t border-ss-border">
          <div className="max-w-7xl mx-auto px-4 py-4">
            {/* Pro Gating Overlay */}
            {showProOverlay && !isPro && (
              <div className="absolute inset-0 bg-ss-bg/95 flex items-center justify-center z-10">
                <div className="text-center">
                  <Lock className="w-8 h-8 text-ss-gold mx-auto mb-3" />
                  <p className="text-ss-text font-semibold mb-2">Preview Complete</p>
                  <p className="text-ss-muted text-sm mb-4">Unlock full Shed Tracks with Pro</p>
                  <Button
                    onClick={() => navigate('/upgrade')}
                    className="bg-ss-gold hover:bg-ss-gold/90 text-black font-semibold"
                  >
                    <Crown className="w-4 h-4 mr-2" />
                    Upgrade to Pro
                  </Button>
                </div>
              </div>
            )}

            <div className="flex items-center gap-6">
              {/* Track Info */}
              <div className="flex-shrink-0 w-48">
                <p className="font-semibold text-ss-text truncate">{currentTrack.title}</p>
                <p className="text-xs text-ss-muted">
                  {currentTrack.key && `Key: ${currentTrack.key}`}
                  {currentTrack.key && currentTrack.tempo && ' • '}
                  {currentTrack.tempo && `${currentTrack.tempo} BPM`}
                </p>
              </div>

              {/* Play/Pause Button */}
              <Button
                onClick={togglePlayPause}
                data-testid="player-play-pause"
                size="lg"
                className="h-12 w-12 rounded-full bg-ss-cyan hover:bg-ss-cyan/90 text-black flex-shrink-0"
              >
                {isPlaying ? (
                  <Pause className="w-5 h-5" />
                ) : (
                  <Play className="w-5 h-5 ml-0.5" />
                )}
              </Button>

              {/* Seek Bar */}
              <div className="flex-1 flex items-center gap-3">
                <span className="text-xs text-ss-muted w-12 text-right">
                  {formatTime(currentTime)}
                </span>
                <div className="flex-1 relative">
                  <Slider
                    value={[currentTime]}
                    max={isPro ? duration : Math.min(duration, FREE_PREVIEW_SECONDS)}
                    step={0.1}
                    onValueChange={handleSeek}
                    className="w-full"
                    data-testid="player-seek"
                  />
                  {/* Preview limit indicator for free users */}
                  {!isPro && duration > FREE_PREVIEW_SECONDS && (
                    <div 
                      className="absolute top-1/2 -translate-y-1/2 w-0.5 h-4 bg-ss-gold"
                      style={{ left: `${(FREE_PREVIEW_SECONDS / duration) * 100}%` }}
                    />
                  )}
                </div>
                <span className="text-xs text-ss-muted w-12">
                  {isPro ? formatTime(duration) : formatTime(Math.min(duration, FREE_PREVIEW_SECONDS))}
                </span>
              </div>

              {/* Volume Control */}
              <div className="flex items-center gap-2 w-32 flex-shrink-0">
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={toggleMute}
                  className="text-ss-muted hover:text-ss-text"
                  data-testid="player-mute"
                >
                  {isMuted ? (
                    <VolumeX className="w-5 h-5" />
                  ) : (
                    <Volume2 className="w-5 h-5" />
                  )}
                </Button>
                <Slider
                  value={[isMuted ? 0 : volume]}
                  max={1}
                  step={0.01}
                  onValueChange={handleVolumeChange}
                  className="w-20"
                  data-testid="player-volume"
                />
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ShedTracksPage;
